/* =========================================================
   Diesel PLP card colour swap (in-place, no navigation)

   Clicking a colour chip on a product card used to navigate to that colour's
   PDP. It now rebuilds the card itself — images, title, price, the ringed
   chip and the quick-add size grid all become the selected colour's, and the
   user stays on the grid.

   WHY A FETCH AND NOT CLIENT-SIDE STITCHING:
   colours here are SEPARATE PRODUCTS grouped by custom.model_code and linked
   through custom.color_siblings, not variants of one product. So "the other
   colour" has its own media, title, price, availability AND its own variant
   map for the quick-add panel. Rendering that product's own card server-side
   (/products/<handle>?view=card -> templates/product.card.liquid) makes all of
   it correct in one step. Patching fields client-side would leave the
   quick-add panel's embedded variant JSON pointing at the previous colour —
   the same class of bug documented at length in color-swatch-swap.js, where a
   partial PDP swap added the wrong product to cart.

   The card markup is replaced wholesale for the same reason: no half-swapped
   state can exist.

   Behaviour is delegated from document so it survives grid re-renders (facet
   filtering, pagination) and works for cards inserted later.
   ========================================================= */
(function () {
  'use strict';

  if (window.__dieselCardSwatchBound) return;
  window.__dieselCardSwatchBound = true;

  var SEL = {
    chip: '[data-card-swatch]',
    card: '.card-wrapper'
  };

  // Per-card request counter, so a fast click-through of several colours always
  // lands on the LAST one clicked rather than whichever response returns last.
  var tokens = new WeakMap();

  function routeRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /* Two of the card params are SECTION settings (image_mode, quick_add) that
     templates/product.card.liquid cannot read, so it renders them at their
     defaults. Carry the outgoing card's actual choices onto the incoming one so
     a swapped card still matches its neighbours in the same grid. */
  function reconcileSectionSettings(incoming, outgoing) {
    var oldMedia = outgoing.querySelector('.card__media');
    var newMedia = incoming.querySelector('.card__media');
    if (oldMedia && newMedia) {
      newMedia.classList.toggle('diesel-card--model', oldMedia.classList.contains('diesel-card--model'));
    }

    if (!outgoing.querySelector('[data-quickadd-open]')) {
      var quickAddParts = incoming.querySelectorAll('.quick-add, [data-quickadd-panel], .dqa-scrim, .dqa-back');
      for (var i = 0; i < quickAddParts.length; i++) quickAddParts[i].remove();
    }
  }

  /* The outgoing card may have its quick-add sheet open, which on mobile also
     means a scrim and a locked body. Drop that state before the node goes away,
     or the lock outlives the element that owned it. */
  function closeQuickAdd(card) {
    card.classList.remove('dqa-open');
    if (!document.querySelector('.card-wrapper.dqa-open')) {
      document.body.classList.remove('dqa-locked');
    }
  }

  function swap(card, handle, token) {
    return fetch(routeRoot() + 'products/' + encodeURIComponent(handle) + '?view=card', {
      headers: { Accept: 'text/html' }
    })
      .then(function (r) {
        if (!r.ok) throw new Error('card fetch failed ' + r.status);
        return r.text();
      })
      .then(function (html) {
        if (token !== tokens.get(card)) return; // a later chip click won
        var incoming = new DOMParser().parseFromString(html, 'text/html').querySelector(SEL.card);
        if (!incoming) throw new Error('no .card-wrapper in ?view=card render');

        reconcileSectionSettings(incoming, card);
        closeQuickAdd(card);
        card.replaceWith(incoming);

        document.dispatchEvent(
          new CustomEvent('dsl:card:swapped', { detail: { handle: handle, card: incoming } })
        );
      });
  }

  document.addEventListener('click', function (e) {
    var chip = e.target.closest && e.target.closest(SEL.chip);
    if (!chip) return;

    // Leave modified clicks alone: ctrl/cmd/shift/middle-click should still
    // open the colour's PDP the way any other link does.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;

    var card = chip.closest(SEL.card);
    if (!card) return;

    e.preventDefault();

    // The ringed chip is the colour already on screen.
    if (chip.classList.contains('is-current')) return;

    var handle = chip.getAttribute('data-swatch-handle');
    if (!handle) return;

    var token = (tokens.get(card) || 0) + 1;
    tokens.set(card, token);

    card.setAttribute('aria-busy', 'true');

    swap(card, handle, token).catch(function (err) {
      // Navigation always yields a correct page, so a failed swap degrades to
      // exactly what the chip did before this script existed.
      console.warn('[dsl-card-swatch] swap failed, navigating:', err);
      window.location.href = chip.getAttribute('href') || routeRoot() + 'products/' + handle;
    });
  });
})();
